/* eslint-disable no-restricted-syntax */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { Plus } from '@saleshandy/icons';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';

import { AccordionCard } from '../../../../../shared/accordion-card/accordioncard';
import StepWrapper from '../../../../../shared/stepwrapper';
import {
  AiSequenceGeneratorData,
  SelectableCard,
  Services,
} from '../../../type';
import AiSequenceModalFooter from '../ai-sequence-modal-footer/ai-sequence-modal-footer';
import {
  resetHasStepSkipped,
  setHasAiSeqStepSkipped,
} from '../../../../../sequence-slice';
import { truncate } from '../../../../../../../shared/utils';

interface KeyOfferingsStepProps {
  handleModalClose?: () => void;
  services?: Services[];
  aiSeqData: AiSequenceGeneratorData;
  saveCurrentStepDetails?: (payload: any) => void;
}

/**
 *
 * @prop services - It will show the Services get from ai.
 * @prop aiSeqUserData - it will show the data which is collected till current step.
 * @prop saveCurrentStepDetails - Function to store current step details.
 *
 * @returns React Component
 */
const KeyOfferingsStep: React.FC<KeyOfferingsStepProps> = ({
  services = [],
  aiSeqData,
  saveCurrentStepDetails = () => {},
}) => {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [items, setItems] = useState<SelectableCard[]>([]);

  const dispatch = useDispatch();

  const itemRefs = useRef<
    Record<number, React.MutableRefObject<() => boolean>>
  >({});

  const handleAddItem = useCallback(() => {
    const newId = Date.now();
    itemRefs.current[newId] = { current: () => true };

    setItems((prev) => [
      ...prev,
      {
        id: newId,
        heading: '',
        description: '',
        isEditable: true,
        isNew: true,
        placeholderHeading: 'Name your primary goal',
        placeholderDescription: 'Describe your primary goal in a few words…',
        isError: false,
      },
    ]);
    setExpandedId(newId);
    setSelectedIds((prev) => [...prev, newId]);
  }, []);

  const handleUpdate = useCallback(
    (id: number, heading: string, description: string) => {
      setItems((prev) =>
        prev?.map((item) =>
          item?.id === id
            ? { ...item, heading, description, isEditable: false }
            : item,
        ),
      );
    },
    [],
  );

  const handleDelete = useCallback((id: number) => {
    setItems((prev) => prev?.filter((item) => item?.id !== id));
    delete itemRefs.current[id];

    setExpandedId((prev) => (prev === id ? null : prev));
    setSelectedIds((prev) => prev?.filter((i) => i !== id));
  }, []);

  const handleSelect = (item: SelectableCard) => {
    setSelectedIds((prev) =>
      prev?.includes(item?.id)
        ? prev?.filter((id) => id !== item?.id)
        : [...prev, item?.id],
    );
  };

  const handleExpand = useCallback((item: SelectableCard) => {
    setExpandedId((prev) => (prev === item.id ? null : item.id));
  }, []);

  const isDisabled = useMemo(
    () => items?.filter((item) => item?.isNew)?.length >= 3,
    [items],
  );

  const selectedItemsHaveError = useMemo(
    () =>
      selectedIds
        ?.map((id) => items?.find((item) => item?.id === id)?.isError)
        ?.some(Boolean),
    [selectedIds, items],
  );

  const handleSaveServices = useCallback(() => {
    const payload = items
      ?.filter((item) => selectedIds?.includes(item.id))
      ?.map((item) => ({
        name: item?.heading?.trim(),
        description: item?.description?.trim(),
        ...(item?.isNew && {
          isCustom: true,
        }),
      }));
    saveCurrentStepDetails({ services: payload });
  }, [items, selectedIds, saveCurrentStepDetails]);

  const handleSkipServices = useCallback(() => {
    dispatch(setHasAiSeqStepSkipped(true));
    saveCurrentStepDetails({ hasSkipped: true });
    dispatch(resetHasStepSkipped());
  }, [saveCurrentStepDetails]);

  useEffect(() => {
    setItems(
      services?.map((service, index) => ({
        id: index + 1,
        heading: service?.name,
        description: service?.description,
        isEditable: true,
        isNew: false,
      })),
    );
  }, [services]);

  return (
    <StepWrapper
      heading={`Key Offerings of ${truncate(
        aiSeqData?.companyInfo?.name,
        26,
        false,
      )}`}
    >
      {items?.map((item) => {
        if (!itemRefs.current[item?.id]) {
          itemRefs.current[item.id] = { current: () => true };
        }

        return (
          <AccordionCard
            key={item?.id}
            type="checkbox"
            selected={selectedIds.includes(item?.id)}
            onSelect={() => handleSelect(item)}
            heading={item?.heading}
            description={item?.description}
            isNew={item?.isNew}
            expanded={expandedId === item?.id}
            onExpand={() => {
              if (expandedId !== null && itemRefs.current[expandedId]) {
                itemRefs.current[expandedId].current?.();
              }
              handleExpand(item);
            }}
            onCollapse={() => {
              itemRefs.current[item.id]?.current?.();
              handleExpand(item);
            }}
            onUpdate={(heading, description) =>
              handleUpdate(item.id, heading, description)
            }
            onDelete={item?.isNew ? () => handleDelete(item?.id) : undefined}
            placeholderHeading="Give your feature a name.."
            placeholderDescription="Tell us what this feature does..."
            validateRef={itemRefs.current[item.id]}
            descriptionLabel="Description"
            headingLabel="Feature"
            dummyHeading="Assign a Title"
            dummyDescription="Tell us what it does"
            onValidationResult={(hasError) => {
              setItems((prev) =>
                prev?.map((el) =>
                  el?.id === item?.id ? { ...el, isError: hasError } : el,
                ),
              );
            }}
          />
        );
      })}
      <div
        className={`add-feature ${isDisabled ? 'disabled' : ''}`}
        onMouseDown={isDisabled ? undefined : handleAddItem}
      >
        <Plus width={16} height={16} />
        <span>Add Feature</span>
      </div>

      <AiSequenceModalFooter
        showSkipBtn={true}
        disableContinueBtn={selectedItemsHaveError || selectedIds?.length === 0}
        onClickContinue={handleSaveServices}
        onClickSkip={handleSkipServices}
      />
    </StepWrapper>
  );
};

export default memo(KeyOfferingsStep);
